import { Grid } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";

import { useProyecto } from "../../data/ProyectoContext";
import { UseSetLenguajePorPais } from "../../hooks/UseSetLenguajePorPais";
import {
  iObjetivoAprendizaje,
  SelectedIndicator,
} from "../../interfaces/ObjetivosAprendizaje/iObjetivosAprendizaje";
import { useActividadesContext } from "../UmmIACategories/Hook/useActividades";
import SingleType from "./SingleType";

import { get_objetivos_aprendizaje_organizacion } from "../../services/api";
import LogoCardSkeleton from "../Loading/LogoCardSkeleton";

type NivelAsignaturaProp = string | { id?: string } | null | undefined;

interface Props {
  idNivelAsignatura: NivelAsignaturaProp;
}

function normalizeId(value: NivelAsignaturaProp): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null && "id" in value) {
    return String((value as any).id ?? "");
  }
  return "";
}

function normalizeIndicadores(value: any): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((x) => String(x ?? "")).filter(Boolean);
}


const CN_FALLBACK = [{
  objetivo: "",
  tipo: "",
  indicadores: [] as string[],
}];

const TP_FALLBACK = [
  {
    objetivo: { id: "", codigo: "", texto: "" },
    aprendizajesEsperados: [
      {
        id: "",
        texto: "",
        criteriosEvaluacion: [
          { id: "", texto: "", objetivosGenericos: [{ id: "", texto: "" }] },
        ],
      },
    ],
  },
];

const GetObjetivosAprendizajeOrganizacion: FC<Props> = ({ idNivelAsignatura }) => {
  const [dataOa, setDataOa] = useState<iObjetivoAprendizaje[]>([]);
  const [selectedIndicators, setSelectedIndicators] =
    useState<SelectedIndicator>({});
  const [expandedPanel, setExpandedPanel] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { proyecto, setProyecto } = useProyecto();
  const { setPromptData, setObjetivosSeleccionados, setIsOAAdded } =
    useActividadesContext();

  UseSetLenguajePorPais();

  const nivelAsignaturaIdStr = useMemo(
    () => normalizeId(idNivelAsignatura),
    [idNivelAsignatura]
  );


  useEffect(() => {
    const orgList = Array.isArray((proyecto as any)?.objetivos_aprendizaje_org)
      ? (proyecto as any).objetivos_aprendizaje_org
      : [];

    const formattedObj = orgList.map((oa: any) => [
      oa.id_objetivo,
      Array.isArray(oa.indicadores) ? oa.indicadores : [],
    ]);

    setSelectedIndicators(Object.fromEntries(formattedObj));
  }, [proyecto]);

  // Fetch objetivos por nivel-asignatura
  useEffect(() => {
    let isMounted = true;

    async function fetchObjectivesByNivelAsignatura() {
      if (!nivelAsignaturaIdStr) {
        if (isMounted) setDataOa([]);
        return;
      }
      setLoading(true);
      try {
        const { data: responseData, status } =
          await get_objetivos_aprendizaje_organizacion(nivelAsignaturaIdStr);

        if (status !== 200) {
          console.error(
            "get_objetivos_aprendizaje_organizacion: status no exitoso",
            { status, responseData }
          );
          if (isMounted) setDataOa([]);
          return;
        }

        const raw = Array.isArray(responseData?.body) ? responseData.body : [];

        const normalized: any[] = raw.map((row: any) => ({
          id_objetivo: String(row?.id_objetivo ?? ""),
          codigo: String(row?.codigo ?? ""),
          objetivo: String(row?.nombre ?? ""),
          nombre: String(row?.nombre ?? ""),

          tipo: String(row?.tipo ?? ""),
          asignatura: String(row?.asignatura ?? ""),
          nivel_org: String(row?.nivel_org ?? ""),

          id_tipo: String(row?.id_tipo ?? ""),
          id_nivel_asignatura: String(row?.id_nivel_asignatura ?? ""),

          indicadores: normalizeIndicadores(row?.indicadores),
        }));

        if (isMounted) {
          setDataOa(normalized as iObjetivoAprendizaje[]);
          setExpandedPanel(null);
        }
      } catch (error) {
        console.error(
          "GetObjetivosAprendizajeOrganizacion: error llamando API",
          error
        );
        if (isMounted) setDataOa([]);
      } finally {
        setLoading(false);
      }
    }

    fetchObjectivesByNivelAsignatura();

    return () => {
      isMounted = false;
    };
  }, [nivelAsignaturaIdStr]);

  const handleIndicatorSelect = (idObjetivo: string, indicador: string) => {
    setSelectedIndicators((prev) => {
      const currentSelection = prev[idObjetivo] || [];
      if (currentSelection.includes(indicador)) {
        return {
          ...prev,
          [idObjetivo]: currentSelection.filter((item) => item !== indicador),
        };
      }
      return {
        ...prev,
        [idObjetivo]: [...currentSelection, indicador],
      };
    });
  };

  const handleToggleAllIndicators = (idObjetivo: string, allIndicators: string[]) => {
    const current = selectedIndicators[idObjetivo] || [];
    const allSelected = current.length === allIndicators.length;

    setSelectedIndicators((prev) => ({
      ...prev,
      [idObjetivo]: allSelected ? [] : allIndicators,
    }));
  };

  const handleAddObjectiveToProject = (objetivo: iObjetivoAprendizaje) => {
    const idObjetivo = objetivo.id_objetivo;
    const selectedObjectiveIndicators = selectedIndicators[idObjetivo] || [];


    setProyecto((prev: any) => {
      const prevOrg = Array.isArray(prev?.objetivos_aprendizaje_org)
        ? prev.objetivos_aprendizaje_org
        : [];

      const exists = prevOrg.some((oa: any) => oa.id_objetivo === idObjetivo);
      if (exists) return prev;

      return {
        ...prev,

        objetivos_aprendizaje_org: [
          ...prevOrg,
          {
            id_objetivo: idObjetivo,
            codigo: objetivo.codigo,
            objetivo: (objetivo as any).objetivo ?? (objetivo as any).nombre ?? "",
            indicadores: selectedObjectiveIndicators,
            tipo: objetivo.tipo,
            materia: objetivo.asignatura,
            grado: (objetivo as any).nivel_org ?? "",
          },
        ],


        objetivos_aprendizaje_cn: prev?.objetivos_aprendizaje_cn || CN_FALLBACK,


        objetivos_aprendizaje_tp: prev?.objetivos_aprendizaje_tp || TP_FALLBACK,
      };
    });

    setPromptData((prevData: any) => {
      const prevOrgPrompt = Array.isArray(prevData?.objetivos_aprendizaje_org)
        ? prevData.objetivos_aprendizaje_org
        : [];

      return {
        ...prevData,

        objetivos_aprendizaje_tp: prevData?.objetivos_aprendizaje_tp || TP_FALLBACK,

        objetivos_aprendizaje_cn: prevData?.objetivos_aprendizaje_cn || CN_FALLBACK,


        objetivos_aprendizaje_org: [
          ...prevOrgPrompt,
          {
            objetivo: (objetivo as any).objetivo ?? (objetivo as any).nombre ?? "",
            tipo: objetivo.tipo,
            indicadores: selectedObjectiveIndicators,
          },
        ],
      };
    });

    setObjetivosSeleccionados((prev) => ({ ...prev, [idObjetivo]: true }));
    setIsOAAdded(true);
  };

  const handleRemoveObjectiveFromProject = (objetivo: iObjetivoAprendizaje) => {
    const idObjetivo = objetivo.id_objetivo;

    setProyecto((prev: any) => {
      const prevOrg = Array.isArray(prev?.objetivos_aprendizaje_org)
        ? prev.objetivos_aprendizaje_org
        : [];

      const updatedOrg = prevOrg.filter((oa: any) => oa.id_objetivo !== idObjetivo);

      if (updatedOrg.length === 0) setIsOAAdded(false);

      return {
        ...prev,

        objetivos_aprendizaje_org: updatedOrg,
      };
    });

    setPromptData((prev: any) => {
      const prevOrgPrompt = Array.isArray(prev?.objetivos_aprendizaje_org)
        ? prev.objetivos_aprendizaje_org
        : [];

      const textoOA = (objetivo as any).objetivo ?? (objetivo as any).nombre ?? "";

      return {
        ...prev,
        objetivos_aprendizaje_org: prevOrgPrompt.filter(
          (o: any) => o.objetivo !== textoOA
        ),
      };
    });

    setObjetivosSeleccionados((prev) => {
      const updated = { ...prev };
      delete updated[idObjetivo];
      return updated;
    });

    setSelectedIndicators((prev) => {
      const updated = { ...prev };
      delete updated[idObjetivo];
      return updated;
    });
  };

  const orderedTypes = useMemo(
  () =>
    [...new Set(dataOa.map((item) => item.tipo))].sort((a, b) =>
      a.localeCompare(b, "es", { sensitivity: "base" })
    ),
  [dataOa]
);

  return (

    <>
      {loading ? (

        <Grid display={"flex"} justifyContent={"center"}>

          <LogoCardSkeleton />

        </Grid>

      ) : (
        <Grid container spacing={4} m={4} mt={-5} maxWidth={"90%"}>
          {orderedTypes.map((category) => (
            <SingleType
              key={category}
              category={category}
              dataOa={dataOa}
              selectedIndicators={selectedIndicators}
              expandedPanel={expandedPanel}
              setExpandedPanel={setExpandedPanel}
              handleIndicatorSelect={handleIndicatorSelect}
              handleAddObjectiveToProject={handleAddObjectiveToProject}
              handleToggleAllIndicators={handleToggleAllIndicators}
              handleRemoveObjectiveFromProject={handleRemoveObjectiveFromProject}
              setSelectedIndicators={setSelectedIndicators}
            />
          ))}
        </Grid>
      )}
    </>

  );
};

export default GetObjetivosAprendizajeOrganizacion;
